import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import AppButton from "../../ui/AppButton";
import { getToken } from "../../config/msalConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URI;

const EVENT_META = {
  start:   { icon: InfoOutlinedIcon,          color: "#3b82f6" },
  info:    { icon: InfoOutlinedIcon,          color: "#64748b" },
  added:   { icon: AddCircleOutlineIcon,      color: "#16a34a" },
  removed: { icon: RemoveCircleOutlineIcon,   color: "#f97316" },
  done:    { icon: CheckCircleOutlineIcon,    color: "#16a34a" },
  error:   { icon: InfoOutlinedIcon,          color: "#ef4444" },
};

function LogLine({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.info;
  const Icon = meta.icon;

  let text = event.message || event.name || "";
  if (event.type === "added")   text = `Added: ${event.name}`;
  if (event.type === "removed") text = `Removed: ${event.name}`;

  return (
    <Box display="flex" alignItems="center" gap={1} py={0.4}>
      <Icon sx={{ fontSize: 16, color: meta.color, flexShrink: 0 }} />
      <Typography fontSize={13} sx={{ color: event.type === "error" ? "#ef4444" : "text.primary" }}>
        {text}
      </Typography>
    </Box>
  );
}

function SummaryChips({ event }) {
  return (
    <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
      <Chip
        label={`${event.added.length} added`}
        size="small"
        sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }}
      />
      <Chip
        label={`${event.removed.length} removed`}
        size="small"
        sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 600 }}
      />
      <Chip
        label={`${event.skipped} already existed`}
        size="small"
        sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600 }}
      />
    </Box>
  );
}

function DepartmentSyncDialog({ open, onClose, onDone }) {
  const [lines, setLines] = useState([]);
  const [isDone, setIsDone] = useState(false);
  const [doneEvent, setDoneEvent] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const logEndRef = useRef(null);
  const abortRef = useRef(null);

  // Auto-scroll log to bottom on new lines
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  // Start streaming when dialog opens
  useEffect(() => {
    if (!open) return;

    setLines([]);
    setIsDone(false);
    setDoneEvent(null);
    setIsRunning(true);

    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${BACKEND}/api/departments/sync/stream`, {
          signal: controller.signal,
          headers: { "oidc-token": token },
        });

        if (!response.ok) {
          setLines((prev) => [...prev, { type: "error", message: `Server error: ${response.status}` }]);
          setIsRunning(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Each SSE message is terminated by "\n\n"
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() ?? "";

          for (const chunk of chunks) {
            for (const line of chunk.split("\n")) {
              if (!line.startsWith("data: ")) continue;
              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === "done") {
                  setDoneEvent(event);
                  setIsDone(true);
                  onDone?.();
                } else {
                  setLines((prev) => [...prev, event]);
                }
              } catch {
                // ignore malformed lines
              }
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setLines((prev) => [...prev, { type: "error", message: "Connection lost" }]);
        }
      } finally {
        setIsRunning(false);
      }
    })();

    return () => controller.abort();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  return (
    <Dialog open={open} onClose={isDone ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Sync Departments from Designations
      </DialogTitle>

      {isRunning && <LinearProgress />}

      <DialogContent sx={{ pt: 1.5 }}>
        <Box
          sx={{
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            minHeight: 180,
            maxHeight: 320,
            overflowY: "auto",
            fontFamily: "monospace",
          }}
        >
          {lines.length === 0 && isRunning && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography fontSize={13} color="text.secondary">Connecting…</Typography>
            </Box>
          )}

          {lines.map((event, idx) => (
            <LogLine key={idx} event={event} />
          ))}

          <div ref={logEndRef} />
        </Box>

        {isDone && doneEvent && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography fontSize={13} fontWeight={700} color="text.primary">
              Sync complete
            </Typography>
            <SummaryChips event={doneEvent} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="contained" onClick={handleClose} disabled={isRunning && !isDone}>
          {isDone ? "Close" : "Cancel"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default DepartmentSyncDialog;
